import { FieldTypeEnum } from '../custom-field.types'
import { ModuleColumnDefinition, ModuleTypeEnum } from '../module'
import { Note, NoteVisibilityEnum } from '../note'

/**
 * Standard width categories for consistent column sizing across note tables
 */
const NOTE_COLUMN_WIDTHS = {
  // Very narrow - icons, small numbers, boolean indicators
  VERY_NARROW: { width: '50px', minWidth: '35px' },
  // Narrow - short text, priorities, small dropdowns
  NARROW: { width: '120px', minWidth: '80px' },
  // Medium - standard text fields, dates, status fields
  MEDIUM: { width: '160px', minWidth: '120px' },
  // Wide - longer text fields, descriptions
  WIDE: { width: '200px', minWidth: '140px' },
  // Very wide - names, main content
  VERY_WIDE: { width: '280px', minWidth: '200px' },
} as const

/**
 * Display names for note visibility levels
 */
export const NOTE_VISIBILITY_DISPLAY_NAMES: Record<NoteVisibilityEnum, string> =
  {
    [NoteVisibilityEnum.PRIVATE]: 'Private',
    [NoteVisibilityEnum.SHARED]: 'Shared',
    [NoteVisibilityEnum.TEAM]: 'Team',
    [NoteVisibilityEnum.RECORD]: 'Record',
  }

/**
 * Colors for note visibility levels
 */
export const NOTE_VISIBILITY_COLORS: Record<NoteVisibilityEnum, string> = {
  [NoteVisibilityEnum.PRIVATE]:
    'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  [NoteVisibilityEnum.SHARED]:
    'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300',
  [NoteVisibilityEnum.TEAM]:
    'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300',
  [NoteVisibilityEnum.RECORD]:
    'bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300',
}

/**
 * Creates standard column definitions for the Note module
 *
 * Editable fields: title, isPinned, color, categoryId
 * Read-only fields: visibility, isCollection, pageCount, moduleType, totalMembers, creatorId, tags, createdAt, updatedAt
 */
export function createNoteStandardColumns(): ModuleColumnDefinition[] {
  return [
    {
      id: 'title',
      name: 'title' as keyof Note,
      label: 'Title',
      description: 'Title of the note',
      isVisible: true,
      isRequired: true,
      isSystem: true,
      isFixed: true,
      order: 0,
      fieldType: FieldTypeEnum.enum.STRING,
      ...NOTE_COLUMN_WIDTHS.VERY_WIDE,
    },
    {
      id: 'visibility',
      name: 'visibility' as keyof Note,
      label: 'Visibility',
      description: 'Who can view this note',
      isVisible: true,
      isRequired: true,
      isSystem: true,
      isFixed: false,
      isReadOnly: true, // Visibility changes require managing members/access, use note detail for this
      order: 1,
      fieldType: FieldTypeEnum.enum.DROPDOWN,
      options: Object.values(NoteVisibilityEnum).map(visibility => ({
        value: visibility,
        label: NOTE_VISIBILITY_DISPLAY_NAMES[visibility],
        color: NOTE_VISIBILITY_COLORS[visibility],
      })),
      ...NOTE_COLUMN_WIDTHS.MEDIUM,
    },
    {
      id: 'isPinned',
      name: 'isPinned' as keyof Note,
      label: 'Pinned',
      description: 'Whether this note is pinned',
      isVisible: true,
      isRequired: false,
      isSystem: true,
      isFixed: false,
      order: 2,
      fieldType: FieldTypeEnum.enum.BOOLEAN,
      ...NOTE_COLUMN_WIDTHS.NARROW,
    },
    {
      id: 'isCollection',
      name: 'isCollection' as keyof Note,
      label: 'Collection',
      description: 'Whether this is a multi-page collection',
      isVisible: false,
      isRequired: false,
      isSystem: true,
      isFixed: false,
      isReadOnly: true, // Computed property, not user-editable
      order: 3,
      fieldType: FieldTypeEnum.enum.BOOLEAN,
      ...NOTE_COLUMN_WIDTHS.NARROW,
    },
    {
      id: 'pageCount',
      name: 'pageCount' as keyof Note,
      label: 'Pages',
      description: 'Number of pages in the collection',
      isVisible: false,
      isRequired: false,
      isSystem: true,
      isFixed: false,
      isReadOnly: true, // Computed property, not user-editable
      order: 4,
      fieldType: FieldTypeEnum.enum.NUMBER,
      ...NOTE_COLUMN_WIDTHS.NARROW,
    },
    {
      id: 'color',
      name: 'color' as keyof Note,
      label: 'Color',
      description: 'Color indicator for the note',
      isVisible: false,
      isRequired: false,
      isSystem: false,
      isFixed: false,
      order: 5,
      fieldType: FieldTypeEnum.enum.STRING,
      ...NOTE_COLUMN_WIDTHS.NARROW,
    },
    {
      id: 'categoryId',
      name: 'categoryId' as keyof Note,
      label: 'Category',
      description: 'Category of the note',
      isVisible: true,
      isRequired: false,
      isSystem: false,
      isFixed: false,
      order: 6,
      fieldType: FieldTypeEnum.enum.DROPDOWN,
      ...NOTE_COLUMN_WIDTHS.MEDIUM,
    },
    {
      id: 'moduleInfo',
      name: 'moduleInfo' as keyof Note,
      label: 'Linked To',
      description: 'Record this note is linked to',
      isVisible: true,
      isRequired: false,
      isSystem: true,
      isFixed: false,
      isReadOnly: false,
      order: 7,
      fieldType: FieldTypeEnum.enum.REFERENCE,
      ...NOTE_COLUMN_WIDTHS.MEDIUM,
    },
    {
      id: 'totalMembers',
      name: 'totalMembers' as keyof Note,
      label: 'Members',
      description: 'Number of members with access',
      isVisible: true,
      isRequired: false,
      isSystem: true,
      isFixed: false,
      isReadOnly: true, // Computed property, not user-editable
      order: 8,
      fieldType: FieldTypeEnum.enum.NUMBER,
      ...NOTE_COLUMN_WIDTHS.NARROW,
    },
    {
      id: 'creatorId',
      name: 'creatorId' as keyof Note,
      label: 'Created By',
      description: 'User who created this note',
      isVisible: true,
      isRequired: false,
      isSystem: true,
      isFixed: false,
      isReadOnly: true, // System-set field, not user-editable
      order: 9,
      fieldType: FieldTypeEnum.enum.USER,
      ...NOTE_COLUMN_WIDTHS.MEDIUM,
    },
    {
      id: 'tags',
      name: 'tags' as keyof Note,
      label: 'Tags',
      description: 'Tags for organizing and categorizing the note',
      isVisible: false,
      isRequired: false,
      isSystem: true,
      isFixed: false,
      isReadOnly: true, // Tags are managed separately, not inline-editable
      order: 90,
      fieldType: FieldTypeEnum.enum.STRING,
      ...NOTE_COLUMN_WIDTHS.WIDE,
    },
    {
      id: 'createdAt',
      name: 'createdAt' as keyof Note,
      label: 'Created Date',
      description: 'Date when note was created',
      isVisible: false,
      isRequired: false,
      isSystem: true,
      isFixed: false,
      isReadOnly: true, // System timestamp, not user-editable
      order: 91,
      fieldType: FieldTypeEnum.enum.DATETIME,
      ...NOTE_COLUMN_WIDTHS.MEDIUM,
    },
    {
      id: 'updatedAt',
      name: 'updatedAt' as keyof Note,
      label: 'Last Updated',
      description: 'Date when note was last updated',
      isVisible: true,
      isRequired: false,
      isSystem: true,
      isFixed: false,
      isReadOnly: true, // System timestamp, not user-editable
      order: 92,
      fieldType: FieldTypeEnum.enum.DATETIME,
      ...NOTE_COLUMN_WIDTHS.MEDIUM,
    },
  ]
}

/**
 * Default standard column definitions for the Note module
 */
export const NOTE_STANDARD_COLUMNS: ModuleColumnDefinition[] =
  createNoteStandardColumns()

/**
 * Helper function to get all available columns for notes,
 * combining standard and custom fields
 */
export function getNoteColumns(
  customFields: any[] = [],
  visibilityPreferences?: Record<string, boolean>,
): ModuleColumnDefinition[] {
  // Start with standard columns
  const standardColumns = createNoteStandardColumns().map(column => ({
    ...column,
    // Override visibility if preferences exist
    isVisible:
      visibilityPreferences?.[column.id] !== undefined
        ? !!visibilityPreferences[column.id]
        : column.isVisible,
  }))

  // Default width for custom field columns based on field type
  const getCustomFieldWidth = (
    fieldType: string,
  ): { width: string; minWidth: string } => {
    switch (fieldType) {
      case FieldTypeEnum.enum.STRING:
        return NOTE_COLUMN_WIDTHS.WIDE
      case FieldTypeEnum.enum.TEXT:
        return NOTE_COLUMN_WIDTHS.WIDE
      case FieldTypeEnum.enum.NUMBER:
        return NOTE_COLUMN_WIDTHS.NARROW
      case FieldTypeEnum.enum.BOOLEAN:
        return NOTE_COLUMN_WIDTHS.NARROW
      case FieldTypeEnum.enum.DATE:
      case FieldTypeEnum.enum.DATETIME:
        return NOTE_COLUMN_WIDTHS.MEDIUM
      case FieldTypeEnum.enum.DROPDOWN:
        return NOTE_COLUMN_WIDTHS.MEDIUM
      case FieldTypeEnum.enum.FILE:
        return NOTE_COLUMN_WIDTHS.WIDE
      case FieldTypeEnum.enum.USER:
        return NOTE_COLUMN_WIDTHS.MEDIUM
      default:
        return NOTE_COLUMN_WIDTHS.MEDIUM
    }
  }

  // Map custom fields to column definitions
  const customColumns = customFields.map((field, index) => {
    const widthSettings = getCustomFieldWidth(field.fieldType)

    return {
      id: `custom_${field.fieldName}`,
      name: field.fieldName,
      label: field.label,
      description: `Custom field: ${field.label}`,
      isVisible:
        visibilityPreferences?.[`custom_${field.fieldName}`] !== undefined
          ? !!visibilityPreferences[`custom_${field.fieldName}`]
          : !!field.isActive,
      isRequired: false,
      isSystem: false,
      order: 93 + index,
      fieldType: field.fieldType,
      width: widthSettings.width,
      minWidth: widthSettings.minWidth,
    }
  }) as ModuleColumnDefinition[]

  // Combine both types of columns
  return [...standardColumns, ...customColumns]
}

/**
 * Get module type for column definitions
 */
export function getNoteModuleType(): ModuleTypeEnum {
  return ModuleTypeEnum.NOTE
}
